import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Alert, Divider, useTheme, useMediaQuery } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { scheduleService, TeamMatchesResponse } from '../services/scheduleService';
import { MatchCard } from './MatchCard';
import { MatchPagination } from './MatchPagination';
import { 
  selectCacheEntry, 
  setCacheEntry, 
  clearExpiredEntries 
} from '../store/teamMatchCacheSlice';
import { RootState } from '../store';

interface ExpandedMatchViewProps {
  teamName: string;
  leagueId: string;
  division?: string;
  teamStats?: {
    won: number;
    drawn: number;
    lost: number;
    goalsFor: number;
    goalsAgainst: number;
    goalDifference: number;
    attScore?: number;
    defScore?: number;
    qualityOfPlay?: number;
  };
  teamId?: string;
  isUserTeam?: boolean;
  /** Hide the mobile stats header (when parent already shows stats) */
  hideStats?: boolean;
  /** Render matches in a two-column grid on desktop */
  twoColumn?: boolean;
  /** Make pagination stick to the bottom of its scroll container */
  stickyPagination?: boolean;
  /** Hide pagination (caller renders it externally via onPaginationInfo) */
  hidePagination?: boolean;
  /** Called when pagination state changes; caller can render MatchPagination externally */
  onPaginationInfo?: (info: { currentPage: number; totalPages: number; disabled: boolean; onPageChange: (page: number) => void } | null) => void;
  /** Hide upcoming matches, only show results */
  hideUpcoming?: boolean;
  /** Unverified match delta for this team */
  unverifiedDelta?: { played: number; won: number; drawn: number; lost: number } | null;
}

export const ExpandedMatchView: React.FC<ExpandedMatchViewProps> = ({
  teamName,
  leagueId,
  division,
  teamStats,
  teamId,
  hideStats,
  twoColumn,
  stickyPagination,
  hidePagination,
  onPaginationInfo,
  hideUpcoming,
  unverifiedDelta
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [matches, setMatches] = useState<TeamMatchesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLoading, setShowLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const matchViewRef = useRef<HTMLDivElement>(null);

  // Check cache for current page
  const cachedEntry = useSelector((state: RootState) => 
    selectCacheEntry(state, leagueId, teamName, currentPage)
  );

  const fetchMatches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Only show loading after 300ms to prevent flash
      const loadingTimer = setTimeout(() => setShowLoading(true), 300);

      const isDesktopTwoCol = twoColumn && !isMobile;
      const data = await scheduleService.getTeamMatches(leagueId, teamName, {
        page: isDesktopTwoCol ? 1 : currentPage,
        limit: isDesktopTwoCol ? 200 : 15,
        division,
        globalTeamId: teamId,
        teamId: teamId
      });

      clearTimeout(loadingTimer);

      // Store in cache
      dispatch(setCacheEntry({
        leagueId,
        teamName,
        page: currentPage,
        data
      }));

      setMatches(data);
    } catch (err: unknown) {
      console.error('Failed to fetch team matches:', err);
      const axiosErr = err as { response?: { data?: { error?: { message?: string } } } };
      setError(axiosErr.response?.data?.error?.message || 'Failed to load matches');
    } finally {
      setLoading(false);
      setShowLoading(false);
    }
  }, [leagueId, teamName, currentPage, division, dispatch, twoColumn, isMobile]);

  useEffect(() => {
    // Clear expired entries on mount
    dispatch(clearExpiredEntries());

    // Check cache first
    if (cachedEntry) {
      setMatches(cachedEntry.data);
      setLoading(false);
      setError(null);
    } else {
      fetchMatches();
    }
  }, [currentPage, teamName, leagueId, division, cachedEntry, dispatch, fetchMatches]);

  const handleRetry = useCallback(() => {
    fetchMatches();
  }, [fetchMatches]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    setTimeout(() => {
      if (!matchViewRef.current) return;
      // Walk up to find the nearest scrollable parent
      let el: HTMLElement | null = matchViewRef.current.parentElement;
      while (el) {
        const { overflowY } = getComputedStyle(el);
        if (overflowY === 'auto' || overflowY === 'scroll') {
          el.scrollTop = 0;
          return;
        }
        el = el.parentElement;
      }
      // No scrollable parent found — scroll the page
      const rect = matchViewRef.current.getBoundingClientRect();
      const appBarHeight = 64;
      const buffer = 8;
      window.scrollTo({
        top: window.scrollY + rect.top - appBarHeight + buffer,
        behavior: 'smooth'
      });
    }, 100);
  }, []);

  // Notify parent of pagination state for external rendering
  useEffect(() => {
    if (!onPaginationInfo) return;
    const isDesktopTwoCol = twoColumn && !isMobile;
    if (!isDesktopTwoCol && matches && matches.pagination.totalPages > 1) {
      onPaginationInfo({
        currentPage: matches.pagination.currentPage,
        totalPages: matches.pagination.totalPages,
        disabled: loading,
        onPageChange: handlePageChange,
      });
    } else {
      onPaginationInfo(null);
    }
  }, [matches, loading, onPaginationInfo, handlePageChange, twoColumn, isMobile]);

  if (showLoading && !matches) {
    // Only show loading on initial load, not on subsequent page changes
    return (
      <Box sx={{ px: isMobile ? 2 : 3, py: isMobile ? 1 : 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Loading matches...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: isMobile ? 2 : 3 }} aria-live="assertive">
        <Alert 
          severity="error" 
          role="alert"
          action={
            <Typography 
              component="button" 
              onClick={handleRetry}
              aria-label="Retry loading matches"
              sx={{ 
                cursor: 'pointer', 
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                color: 'inherit',
                minHeight: 44, // Touch-friendly target
                minWidth: 44
              }}
            >
              Retry
            </Typography>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!matches) {
    return null;
  }

  const { matches: matchData, pagination } = matches;

  // Reclassify past matches that aren't completed/cancelled/postponed as "played but unscored"
  const now = new Date();
  const allUpcoming = [...matchData.upcoming, ...matchData.pendingTBD];
  const upcomingMatches: typeof matchData.upcoming = [];
  const playedUnscored: typeof matchData.completed = [];
  for (const m of allUpcoming) {
    const isPast = m.dateTime && new Date(m.dateTime) < now;
    const isActiveStatus = m.status !== 'CANCELLED' && m.status !== 'POSTPONED';
    if (isPast && isActiveStatus) {
      playedUnscored.push(m);
    } else {
      upcomingMatches.push(m);
    }
  }
  // Merge played-unscored into completed, sorted newest first
  const resultsMatches = [...playedUnscored, ...matchData.completed].sort((a, b) => {
    const aTime = a.dateTime ? new Date(a.dateTime).getTime() : 0;
    const bTime = b.dateTime ? new Date(b.dateTime).getTime() : 0;
    return bTime - aTime;
  });
  const totalMatches = resultsMatches.length + upcomingMatches.length;

  if (totalMatches === 0) {
    return (
      <Box sx={{ px: isMobile ? 2 : 3, py: isMobile ? 1 : 2, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No matches found for {teamName}
        </Typography>
      </Box>
    );
  }

  const renderMatchList = (matchList: typeof matchData.completed, label: string) => (
    <Box role="list" aria-label={label}>
      {matchList.map((match, index) => (
        <Box key={match.id} role="listitem">
          <MatchCard match={match} highlightTeam={teamName} />
          {index < matchList.length - 1 && <Divider />}
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      ref={matchViewRef}
      sx={{
        px: isMobile ? 2 : 3,
        py: isMobile ? 1 : 2,
        overflowX: 'hidden' // Prevent horizontal scrollbar during animations
      }}
      role="region"
      aria-label={`Match history for ${teamName}`}
    >
      {/* Mobile stats header */}
      {!hideStats && isMobile && teamStats && (
        <Box sx={{ mb: 0, mx: -2, mt: -1, textAlign: 'center', py: 1, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-around', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>GP</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{teamStats.won + teamStats.drawn + teamStats.lost}{unverifiedDelta && unverifiedDelta.played > 0 && <Box component="span" sx={{ color: 'warning.main' }}>({unverifiedDelta.played})</Box>}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>W</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{teamStats.won}{unverifiedDelta && unverifiedDelta.won > 0 && <Box component="span" sx={{ color: 'warning.main' }}>({unverifiedDelta.won})</Box>}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>D</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{teamStats.drawn}{unverifiedDelta && unverifiedDelta.drawn > 0 && <Box component="span" sx={{ color: 'warning.main' }}>({unverifiedDelta.drawn})</Box>}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>L</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{teamStats.lost}{unverifiedDelta && unverifiedDelta.lost > 0 && <Box component="span" sx={{ color: 'warning.main' }}>({unverifiedDelta.lost})</Box>}</Typography>
            </Box>
            {teamStats.qualityOfPlay !== undefined ? (
              <>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>Att</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{teamStats.attScore ?? 0}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>Def</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{teamStats.defScore ?? 0}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>GD</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {teamStats.goalDifference > 0 ? '+' : ''}{teamStats.goalDifference}
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>GF</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{teamStats.goalsFor}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>GA</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{teamStats.goalsAgainst}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>GD</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {teamStats.goalDifference > 0 ? '+' : ''}{teamStats.goalDifference}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {!hideUpcoming && upcomingMatches.length > 0 && (
        <>
          {renderMatchList(upcomingMatches, 'Upcoming matches')}
          {resultsMatches.length > 0 && <Divider sx={{ my: 1 }} />}
        </>
      )}
      {resultsMatches.length > 0 && renderMatchList(resultsMatches, 'Completed matches')}

      {/* Pagination - only show on mobile when there are multiple pages */}
      {!hidePagination && !(twoColumn && !isMobile) && pagination.totalPages > 1 && (
        stickyPagination ? (
          <Box sx={{
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            py: 0.5,
            zIndex: 1,
          }}>
            <MatchPagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              disabled={loading}
            />
          </Box>
        ) : (
          <MatchPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
            disabled={loading}
          />
        )
      )}
    </Box>
  );
};
